/**
 * Detects WhatsApp / simplified embed mode.
 * Supports:
 * - WA-prefixed service routes: #/Services/wconfirmbenefits (and future #/Services/w*)
 * - Hash query: #/Services/confirmbenefits?wa=1
 * - Search before hash: ?wa=1#/Services/...
 *
 * Works with both window.location (hash in .hash) and react-router
 * HashRouter location (path in .pathname).
 */
export function isWhatsAppMode(location) {
  if (typeof window === "undefined") {
    return false;
  }

  const loc = location || window.location;
  const hash = loc.hash || (typeof window !== "undefined" ? window.location.hash : "") || "";
  const pathname = (loc.pathname || "").toLowerCase();
  const hashPath = hash.replace(/^#/, "").split("?")[0].toLowerCase();

  // e.g. /Services/wconfirmbenefits, /services/wsubmitclaim
  const waServicePath = /\/services\/w[a-z0-9_-]*/i;
  if (waServicePath.test(pathname) || waServicePath.test(hashPath)) {
    return true;
  }

  try {
    const searchParams = new URLSearchParams(loc.search || "");
    if (searchParams.get("wa") === "1") {
      return true;
    }

    const hashQueryIndex = hash.indexOf("?");
    if (hashQueryIndex !== -1) {
      const hashParams = new URLSearchParams(hash.substring(hashQueryIndex));
      if (hashParams.get("wa") === "1") {
        return true;
      }
    }
  } catch (e) {
    // ignore parse errors
  }

  return false;
}

/**
 * Attempt to close the WhatsApp in-app browser / webview.
 * Many WebViews block window.close() unless the page was opened via window.open.
 * Falls back to history.back / about:blank. Callers should show a short hint
 * if the page is still visible after a moment.
 */
export function closeWhatsAppView() {
  const tryClose = () => {
    try {
      window.close();
    } catch (e) {
      // ignored
    }
  };

  tryClose();

  // Some Android WebViews honor open('', '_self') then close
  try {
    window.open("", "_self");
    tryClose();
  } catch (e) {
    // ignored
  }

  setTimeout(() => {
    try {
      if (typeof window.closed === "boolean" && window.closed) {
        return;
      }
    } catch (e) {
      // ignored
    }

    try {
      if (window.history.length > 1) {
        window.history.go(-1);
        return;
      }
    } catch (e) {
      // ignored
    }

    try {
      window.location.replace("about:blank");
    } catch (e) {
      // ignored
    }
  }, 100);
}

/** Shared visitor-facing copy when member details do not match / API rejects. */
export const VISITOR_DETAILS_ERROR =
  "Please confirm that the details you entered are correct and try again.";

export default isWhatsAppMode;
